/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React from "react";
import { X, ArrowUpDown } from "lucide-react";
import { ScopeSelect, REGION_LABELS } from "../ScopeSelect";
import { CreatedFilter } from "./CreatedFilter";
import { SERVICE_LABEL } from "./SignedBits";
import { TEAMS } from "../../data/constants";

export const SIGNED_DATE_MODES = [
  { id: "", zh: "签约时间：全部", en: "Signed: any" },
  { id: "week", zh: "本周签约", en: "Signed this week" },
  { id: "month", zh: "本月签约", en: "Signed this month" },
  { id: "lastMonth", zh: "上月签约", en: "Signed last month" },
  { id: "quarter", zh: "本季度签约", en: "This quarter" },
  { id: "30d", zh: "近 30 天签约", en: "Last 30 days" },
  { id: "year", zh: "今年签约", en: "This year" },
  { id: "custom", zh: "自定义时间段…", en: "Custom range…" },
];
const COLLECT = [
  { id: "", zh: "收款状态：全部", en: "Collection: all" },
  { id: "unpaid", zh: "有未收款", en: "Has unpaid" },
  { id: "due", zh: "已开票未收", en: "Invoiced, unpaid" },
  { id: "overdue", zh: "有逾期", en: "Overdue" },
  { id: "paid", zh: "已收齐", en: "Fully paid" },
];
const SORTS = [
  { id: "", zh: "排序：签约日 新→旧", en: "Sort: signed newest" },
  { id: "signed_asc", zh: "签约日 旧→新", en: "Signed oldest" },
  { id: "unpaid_desc", zh: "未收金额 高→低", en: "Unpaid high→low" },
  { id: "due_desc", zh: "待收金额 高→低", en: "Due now high→low" },
  { id: "contract_desc", zh: "合同额 高→低", en: "Contract high→low" },
];
// 施工组纳入统一筛选键，确保清除、保存常用筛选及详情返回都保留同一筛选口径。
export const SIGNED_FILTER_KEYS = ["region", "team", "sales", "service", "signed", "sfrom", "sto", "collect", "sort"];
const sel = "text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 focus:border-indigo-400 focus:outline-none";
const on = "border-indigo-400 text-indigo-800 bg-indigo-50/60";

/* 已签约客户 · 快速筛选栏：区域 / 施工组 / 负责销售 / 服务类型 / 签约时间 / 收款状态 / 排序，全部写入 URL 参数。 */
export const SignedQuickFilters = ({ zh, params, setParams, facets, showRegion, showSales, count, className = "flex flex-wrap items-center gap-2" }) => {
  const get = (k) => params.get(k) || "";
  const set = (patch) => { const p = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k))); setParams(p, { replace: true }); };
  const active = SIGNED_FILTER_KEYS.filter((k) => get(k) && k !== "sort");
  // 搜索无结果时可选组会变空，仍保留当前已选组，避免下拉变空而用户看不出哪个条件在生效。
  const teams = [...(facets.teams || [])];
  if (get("team") && get("team") !== "__unassigned__" && !teams.some((t) => t.id === get("team"))) teams.push({ id: get("team") });
  const Sel = ({ k, options, tid, label }) => (
    <select data-testid={tid} className={`${sel} ${get(k) ? on : ""}`} value={get(k)} onChange={(e) => set({ [k]: e.target.value })}>
      {options.map((o) => <option key={o.id} value={o.id}>{o.id ? (zh ? o.zh : o.en) : label}</option>)}
    </select>
  );
  return (
    <div className={className} data-testid="signed-quick-filters" data-active={active.length}>
      {showRegion && (
        <ScopeSelect kind="region" data-testid="signed-filter-region" className={`${sel} ${get("region") ? on : ""}`} value={get("region")} onChange={(e) => set({ region: e.target.value })}>
          <option value="">{zh ? "全部区域" : "All regions"}</option>
          {(facets.regions || []).map((r) => <option key={r} value={r}>{(REGION_LABELS[r] || {})[zh ? "zh" : "en"] || r}</option>)}
        </ScopeSelect>
      )}
      {/* 只展示后台按客户读取权限返回的施工组，不按账号分公司再次收窄，保留财务跨公司查看能力。 */}
      <select aria-label={zh ? "施工组" : "Construction team"} data-testid="signed-filter-team"
        className={`${sel} min-h-11 md:min-h-0 max-w-full ${get("team") ? on : ""}`} value={get("team")} onChange={(e) => set({ team: e.target.value })}>
        <option value="">{zh ? "全部施工组" : "All construction teams"}</option>
        {teams.map((t) => {
          const known = TEAMS.find((item) => item.id === t.id);
          return <option key={t.id} value={t.id}>{(zh ? known?.zh : known?.name) || t.name || t.id}</option>;
        })}
        {(facets.hasUnassignedTeam || get("team") === "__unassigned__") && <option value="__unassigned__">{zh ? "未分配施工组" : "Unassigned team"}</option>}
      </select>
      {showSales && (
        <select data-testid="signed-filter-sales" className={`${sel} ${get("sales") ? on : ""}`} value={get("sales")} onChange={(e) => set({ sales: e.target.value })}>
          <option value="">{zh ? "全部销售" : "All sales"}</option>
          {(facets.sales || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      <select data-testid="signed-filter-service" className={`${sel} ${get("service") ? on : ""}`} value={get("service")} onChange={(e) => set({ service: e.target.value })}>
        <option value="">{zh ? "全部服务类型" : "All services"}</option>
        {(facets.services || []).map((s) => <option key={s} value={s}>{(SERVICE_LABEL[s] || [])[zh ? 0 : 1] || s}</option>)}
      </select>
      <CreatedFilter zh={zh} modes={SIGNED_DATE_MODES} testId="signed-filter-signed" className={sel} count={count} unit={zh ? "户" : "customers"} title={zh ? "按签约日筛选" : "Filter by signed date"}
        value={{ mode: get("signed"), from: get("sfrom"), to: get("sto") }} onChange={(v) => set({ signed: v.mode, sfrom: v.mode === "custom" ? v.from : "", sto: v.mode === "custom" ? v.to : "" })} />
      <Sel k="collect" options={COLLECT} tid="signed-filter-collect" label={zh ? "收款状态：全部" : "Collection: all"} />
      <span className="inline-flex items-center"><ArrowUpDown className="h-3.5 w-3.5 text-slate-400 -mr-6 ml-2 relative z-10 pointer-events-none" /><select data-testid="signed-filter-sort" className={`${sel} pl-7 ${get("sort") ? on : ""}`} value={get("sort")} onChange={(e) => set({ sort: e.target.value })}>
        {SORTS.map((o) => <option key={o.id} value={o.id}>{zh ? o.zh : o.en}</option>)}
      </select></span>
      {active.length > 0 && (
        <button type="button" data-testid="signed-filter-clear" onClick={() => set(Object.fromEntries(SIGNED_FILTER_KEYS.map((k) => [k, ""])))} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-50">
          <X className="h-3.5 w-3.5" />{zh ? `清除筛选（${active.length}）` : `Clear (${active.length})`}
        </button>
      )}
    </div>
  );
};
